import {
  AssetData,
  ImageAssetData,
  SavedAssetData,
  VideoAssetData,
} from '@superfunnel/types';
import Modal from '@superfunnel/ui-core/Modal';
import FieldTitle from '@superfunnel/ui-core/v2/FieldTitle';
import Input from '@superfunnel/ui-core/v2/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import {
  formFieldRequiredValidator,
  formFieldUrlValidator,
} from '@superfunnel/utils';
import { collection, doc, setDoc, Timestamp } from 'firebase/firestore';
import { useCallback, useContext, useEffect, useState } from 'react';
import FileUploadInput from '../../components/FormFields/FileUpload/FileUploadInput';
import { AuthContext } from '../../contexts/AuthContext';
import { UserContext } from '../../contexts/UserContext';
import { db } from '../../Firebase';

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (image: AssetData) => void;
}

export default function AddEmbededVideoAssetModal({
  open,
  onClose,
  onSave,
}: Props) {
  const { firebaseUser } = useContext(AuthContext);
  const { selectedAccount } = useContext(UserContext);

  const [name, setName] = useState<string>('');
  const [source, setSource] = useState<'vimeo'>('vimeo');
  const [thumbnail, setThumbnail] = useState<ImageAssetData>();
  const [link, setLink] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const onAddClick = useCallback(async () => {
    if (!selectedAccount) return;
    const accountId = selectedAccount.id;

    const docRef = doc(collection(db, `accounts/${accountId}/assets`));
    const data: VideoAssetData = {
      id: docRef.id,
      source: source,
      url: link,
      type: 'video',
      thumbnailImage: thumbnail,
      alt: name,
    };
    const savedAssetData: SavedAssetData = {
      ...data,
      createdAt: Timestamp.now(),
      createdBy: firebaseUser?.uid ?? '',
      name: link,
    };

    try {
      setLoading(true);
      await setDoc(docRef, savedAssetData);
      onSave(savedAssetData);
      onClose();
    } catch (e: any) {
      setError(e.message);
      setLoading(false);
    }
  }, [
    selectedAccount,
    source,
    link,
    thumbnail,
    firebaseUser?.uid,
    name,
    onSave,
    onClose,
  ]);

  useEffect(() => {
    if (!selectedAccount) return;
    if (!open) {
      setSource('vimeo');
      setLink('');
      setLoading(false);
      setError(undefined);
      return;
    }
  }, [open, selectedAccount]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add Embeded Video"
      actions={[
        {
          title: 'Cancel',
          type: 'secondary',
          disabled: loading,
          onClick: onClose,
        },
        {
          title: 'Create',
          type: 'primary',
          loading: loading,
          disabled: loading || !thumbnail || !link || !source || !name,
          onClick: onAddClick,
        },
      ]}
    >
      <div className="mt-0 flex flex-col gap-4">
        <Input
          title="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          validation={formFieldRequiredValidator(name)}
        ></Input>
        <div className="flex flex-col justify-start gap-1">
          <FieldTitle title="Source" />
          <Select value={source} onValueChange={(s: 'vimeo') => setSource(s)}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="vimeo">Vimeo</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Input
          title="Link"
          value={link}
          onChange={(e) => setLink(e.target.value)}
          validation={formFieldUrlValidator(link, true)}
        ></Input>

        <FieldTitle className="mt-4" title="Thumbnail Image" />
        <FileUploadInput
          image={thumbnail}
          onSelected={(image) => {
            if (image && image.type !== 'image') return;

            setThumbnail(image);
          }}
          showImageSettings
          types={['image']}
        />
      </div>
      {error ? (
        <p className="mt-4 w-full text-end text-sm text-red-600">{error}</p>
      ) : (
        <></>
      )}
    </Modal>
  );
}
